<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";

const keys = ref([]),
  users = ref([]),
  operations = ref({});
const selected = ref(null),
  editing = ref(false),
  busy = ref(false),
  error = ref("");
const secret = ref(""),
  search = ref(""),
  events = ref([]),
  approvals = ref([]);
const auditSearch = ref("");
const activityView = ref("access");
const page = ref(1),
  lastPage = ref(1),
  outcome = ref("");
const form = ref(emptyForm());
function emptyForm() {
  const expiry = new Date(Date.now() + 30 * 86400000);
  return {
    name: "",
    description: "",
    user_id: "",
    expires_at: new Date(expiry.getTime() - expiry.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16),
    permissions: [],
    approval_permissions: [],
    ip_allowlist: "",
    requests_per_minute: 60,
  };
}
const groups = computed(() => {
  const result = {};
  for (const [id, op] of Object.entries(operations.value)) {
    if (
      search.value &&
      !`${op.module} ${op.label}`
        .toLowerCase()
        .includes(search.value.toLowerCase())
    )
      continue;
    (result[op.module] ||= []).push({ id, ...op });
  }
  return Object.entries(result).sort(([a], [b]) => a.localeCompare(b));
});
const label = (id) =>
  operations.value[id]
    ? `${operations.value[id].module} · ${operations.value[id].label}`
    : id;
const date = (value) =>
  value ? new Date(value).toLocaleString("en-GB") : "Never";
const status = (key) =>
  key.revoked_at
    ? "Revoked"
    : new Date(key.expires_at) <= new Date()
      ? "Expired"
      : key.user?.active
        ? "Active"
        : "Owner disabled";
async function run(action) {
  busy.value = true;
  error.value = "";
  try {
    await action();
  } catch (e) {
    error.value = e.response?.data?.message || "The request failed.";
  } finally {
    busy.value = false;
  }
}
async function load() {
  const { data } = await apiClient.get("/api/super/agent-keys", {
    suppressErrorAlert: true,
  });
  keys.value = data.keys;
  users.value = data.users;
  operations.value = data.operations;
}
function create() {
  selected.value = null;
  editing.value = true;
  form.value = emptyForm();
  secret.value = "";
}
function edit(key) {
  selected.value = key;
  editing.value = true;
  secret.value = "";
  const expiry = new Date(key.expires_at);
  form.value = {
    ...key,
    expires_at: new Date(expiry.getTime() - expiry.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16),
    permissions: [...key.permissions],
    approval_permissions: [...key.approval_permissions],
    ip_allowlist: (key.ip_allowlist || []).join("\n"),
  };
}
function preset(mode) {
  form.value.permissions = Object.entries(operations.value)
    .filter(
      ([, op]) =>
        mode === "all" ||
        (mode === "read" && op.read) ||
        (mode === "operational" && !op.sensitive),
    )
    .map(([id]) => id);
  form.value.approval_permissions = form.value.approval_permissions.filter(
    (id) => form.value.permissions.includes(id),
  );
}
function toggleGroup(ops, read, checked) {
  const ids = ops.filter((op) => op.read === read).map((op) => op.id);
  form.value.permissions = checked
    ? [...new Set([...form.value.permissions, ...ids])]
    : form.value.permissions.filter((id) => !ids.includes(id));
  form.value.approval_permissions = form.value.approval_permissions.filter(
    (id) => form.value.permissions.includes(id),
  );
}
function save() {
  run(async () => {
    const payload = {
      name: form.value.name,
      description: form.value.description,
      user_id: Number(form.value.user_id),
      expires_at: new Date(form.value.expires_at).toISOString(),
      permissions: form.value.permissions,
      approval_permissions: form.value.approval_permissions.filter((id) =>
        form.value.permissions.includes(id),
      ),
      requests_per_minute: Number(form.value.requests_per_minute),
      ip_allowlist: form.value.ip_allowlist
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    };
    const { data } = selected.value
      ? await apiClient.put(
          `/api/super/agent-keys/${selected.value.id}`,
          payload,
        )
      : await apiClient.post("/api/super/agent-keys", payload);
    secret.value = data.secret || "";
    editing.value = false;
    await load();
    selected.value = keys.value.find((k) => k.id === data.key.id);
    await loadActivity();
  });
}
async function loadActivity() {
  if (!selected.value) return;
  const [audit, pending] = await Promise.all([
    apiClient.get(`/api/super/agent-keys/${selected.value.id}/activity`, {
      params: {
        page: page.value,
        view: activityView.value,
        outcome: outcome.value || undefined,
        q: auditSearch.value || undefined,
      },
    }),
    apiClient.get(`/api/super/agent-keys/${selected.value.id}/approvals`),
  ]);
  events.value = audit.data.data;
  lastPage.value = audit.data.last_page;
  approvals.value = pending.data;
}
function view(key) {
  selected.value = key;
  editing.value = false;
  secret.value = "";
  page.value = 1;
  run(loadActivity);
}
function action(key, name) {
  if (
    !window.confirm(
      `${name === "revoke" ? "Revoke" : "Rotate"} “${key.name}”? Existing credentials and browser sessions will stop working.`,
    )
  )
    return;
  run(async () => {
    const { data } = await apiClient.post(
      `/api/super/agent-keys/${key.id}/${name}`,
    );
    secret.value = data.secret || "";
    await load();
    selected.value = keys.value.find((k) => k.id === key.id);
    await loadActivity();
  });
}
function review(approval, decision) {
  run(async () => {
    await apiClient.post(
      `/api/super/agent-keys/${selected.value.id}/approvals/${approval.id}`,
      { decision },
    );
    await loadActivity();
  });
}
function approveSensitive(checked) {
  form.value.approval_permissions = checked
    ? form.value.permissions.filter((id) => operations.value[id]?.sensitive)
    : [];
}
onMounted(() => run(load));
</script>

<template>
  <main class="space-y-6">
    <div class="box flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold">Agent access</h1>
        <p class="mt-1 text-sm text-slate-500">
          Named keys, scoped permissions and an activity trail for every agent.
        </p>
      </div>
      <button class="btn" :disabled="busy" @click="create">
        Create access key
      </button>
    </div>
    <p v-if="error" role="alert" class="rounded bg-red-100 p-4 text-red-800">
      {{ error }}
    </p>
    <div v-if="secret" class="box border border-amber-400 space-y-3">
      <h2 class="font-semibold">Copy this key now</h2>
      <p class="text-sm">
        It will only be shown once. Store it in the agent’s secret store.
      </p>
      <code
        class="block break-all select-all rounded bg-slate-100 p-3 text-slate-900"
        >{{ secret }}</code
      >
      <button class="btn" @click="secret = ''">I have saved the key</button>
    </div>
    <form v-if="editing" class="box space-y-5" @submit.prevent="save">
      <h2 class="text-xl font-semibold">
        {{ selected ? "Edit key" : "Create key" }}
      </h2>
      <div class="grid gap-4 md:grid-cols-2">
        <label
          >Name<input
            v-model="form.name"
            required
            maxlength="120"
            class="agent-input"
        /></label>
        <label
          >Act on behalf of<select
            v-model="form.user_id"
            required
            :disabled="!!selected"
            class="agent-input"
          >
            <option disabled value="">Select responsible user</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.name }} · {{ user.email }}
            </option></select
          ><span class="text-xs text-slate-500"
            >The key is also limited by this user’s current roles.</span
          ></label
        >
        <label
          >Expires at<input
            v-model="form.expires_at"
            type="datetime-local"
            required
            class="agent-input"
        /></label>
        <label
          >Requests per minute<input
            v-model="form.requests_per_minute"
            type="number"
            min="1"
            max="600"
            required
            class="agent-input"
        /></label>
        <label
          >Description<textarea
            v-model="form.description"
            maxlength="500"
            class="agent-input"
          />
        </label>
        <label
          >Allowed IP addresses or CIDR ranges<textarea
            v-model="form.ip_allowlist"
            placeholder="Optional, one per line"
            class="agent-input"
          />
        </label>
      </div>
      <div class="space-y-3">
        <h3 class="font-semibold">
          Permissions · {{ form.permissions.length }} selected
        </h3>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="btn" @click="preset('read')">
            Read only</button
          ><button type="button" class="btn" @click="preset('operational')">
            Routine operations</button
          ><button type="button" class="btn" @click="preset('all')">
            All current operations</button
          ><button type="button" class="btn" @click="preset('none')">
            Clear
          </button>
        </div>
        <p class="text-sm text-slate-500">
          New operations require a new grant. Access-key management and audit
          administration remain reserved for human administrators.
        </p>
        <label class="flex items-center gap-2"
          ><input
            type="checkbox"
            @change="approveSensitive($event.target.checked)"
          />Require approval for selected sensitive actions</label
        >
        <input
          v-model="search"
          type="search"
          placeholder="Find an app or action"
          class="agent-input"
        />
        <div
          class="max-h-[32rem] overflow-auto rounded border border-slate-200 dark:border-slate-700"
        >
          <details
            v-for="[module, ops] in groups"
            :key="module"
            class="border-b p-3 dark:border-slate-700"
          >
            <summary class="cursor-pointer font-medium">
              {{ module }}
              <span class="text-sm text-slate-500"
                >{{
                  ops.filter((op) => form.permissions.includes(op.id)).length
                }}/{{ ops.length }}</span
              >
            </summary>
            <div class="my-3 flex gap-5">
              <label
                v-for="read in [true, false]"
                :key="String(read)"
                class="flex items-center gap-2"
                ><input
                  type="checkbox"
                  :checked="
                    ops.filter((op) => op.read === read).length > 0 &&
                    ops
                      .filter((op) => op.read === read)
                      .every((op) => form.permissions.includes(op.id))
                  "
                  @change="toggleGroup(ops, read, $event.target.checked)"
                />{{ read ? "All reads" : "All writes" }}</label
              >
            </div>
            <div
              v-for="op in ops"
              :key="op.id"
              class="flex flex-wrap items-center justify-between gap-3 py-1.5 text-sm"
            >
              <label class="flex items-center gap-2"
                ><input
                  v-model="form.permissions"
                  type="checkbox"
                  :value="op.id"
                />{{ op.label }}
                <span class="text-xs text-slate-500"
                  >{{ op.tool ? "Agent tool" : op.read ? "Read" : "Write"
                  }}{{ op.sensitive ? " · Sensitive" : "" }}</span
                ></label
              >
              <label
                v-if="form.permissions.includes(op.id)"
                class="flex items-center gap-2 text-xs"
                ><input
                  v-model="form.approval_permissions"
                  type="checkbox"
                  :value="op.id"
                />Human approval</label
              >
            </div>
          </details>
        </div>
      </div>
      <div class="flex gap-3">
        <button class="btn" :disabled="busy || !form.permissions.length">
          {{ busy ? "Saving…" : "Save key" }}</button
        ><button type="button" class="btn" @click="editing = false">
          Cancel
        </button>
      </div>
    </form>
    <div class="box overflow-x-auto">
      <table class="w-full text-left text-sm">
        <thead>
          <tr>
            <th class="p-2">Key</th>
            <th>Responsible user</th>
            <th>Status</th>
            <th>Expires</th>
            <th>Last used</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="key in keys"
            :key="key.id"
            class="border-t dark:border-slate-700"
          >
            <td class="p-2">
              <button
                class="font-medium text-cyan-700 dark:text-cyan-300"
                @click="view(key)"
              >
                {{ key.name }}
              </button>
              <p class="text-xs text-slate-500">
                {{ key.prefix }}… · {{ key.permissions.length }} permissions
              </p>
            </td>
            <td>{{ key.user?.name }}</td>
            <td>{{ status(key) }}</td>
            <td>{{ date(key.expires_at) }}</td>
            <td>{{ date(key.last_used_at) }}</td>
            <td>
              <div class="flex flex-wrap gap-3">
                <button @click="view(key)">Activity</button
                ><button v-if="!key.revoked_at" @click="edit(key)">Edit</button
                ><button
                  v-if="status(key) === 'Active'"
                  :disabled="busy"
                  @click="action(key, 'rotate')"
                >
                  Rotate</button
                ><button
                  v-if="!key.revoked_at"
                  class="text-red-600"
                  :disabled="busy"
                  @click="action(key, 'revoke')"
                >
                  Revoke
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!keys.length">
            <td colspan="6" class="p-6 text-slate-500">No agent keys yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <template v-if="selected && !editing">
      <section class="box space-y-4">
        <h2 class="text-xl font-semibold">{{ selected.name }} · Approvals</h2>
        <p v-if="!approvals.length" class="text-sm text-slate-500">
          No approval requests.
        </p>
        <div
          v-for="approval in approvals"
          :key="approval.id"
          class="rounded border p-3 dark:border-slate-700"
        >
          <div class="flex flex-wrap justify-between gap-3">
            <p>
              #{{ approval.id }} · {{ label(approval.operation) }} ·
              {{ approval.state }}
            </p>
            <p class="text-xs text-slate-500">
              Expires {{ date(approval.expires_at) }}
            </p>
          </div>
          <details class="my-2">
            <summary class="cursor-pointer text-sm">
              Review exact request
            </summary>
            <pre
              class="mt-2 overflow-auto whitespace-pre-wrap break-words text-xs"
              >{{ JSON.stringify(approval.payload, null, 2) }}</pre
            >
          </details>
          <div
            v-if="
              approval.state === 'pending' &&
              new Date(approval.expires_at) > new Date()
            "
            class="flex gap-4"
          >
            <button
              class="btn"
              :disabled="busy"
              @click="review(approval, 'approved')"
            >
              Approve once</button
            ><button
              class="text-red-600"
              :disabled="busy"
              @click="review(approval, 'rejected')"
            >
              Reject
            </button>
          </div>
        </div>
      </section>
      <section class="box space-y-4">
        <div class="flex flex-wrap justify-between gap-4">
          <h2 class="text-xl font-semibold">
            {{ selected.name }} · Agent activity
          </h2>
          <div class="flex gap-3">
            <input
              v-model="auditSearch"
              type="search"
              placeholder="Find endpoint, app, tool or request ID"
              class="agent-input"
              @keyup.enter="
                page = 1;
                run(loadActivity);
              "
            /><select
              v-model="outcome"
              class="agent-input"
              @change="
                page = 1;
                run(loadActivity);
              "
            >
              <option value="">All outcomes</option>
              <option
                v-for="item in [
                  'succeeded',
                  'denied',
                  'failed',
                  'started',
                  'pending',
                ]"
                :key="item"
              >
                {{ item }}
              </option></select
            ><button class="btn" :disabled="busy" @click="run(loadActivity)">
              Refresh
            </button>
          </div>
        </div>
        <label class="flex flex-wrap items-center gap-3 text-sm">
          Show
          <select
            v-model="activityView"
            class="agent-input max-w-xs"
            @change="
              page = 1;
              run(loadActivity);
            "
          >
            <option value="access">Endpoint requests</option>
            <option value="all">Requests and security events</option>
          </select>
        </label>
        <p class="text-sm text-slate-500">
          Requests made using this key, including failed and denied access.
          Refreshing this page does not create activity entries.
        </p>
        <p v-if="!events.length" class="text-sm text-slate-500">
          {{
            activityView === "access"
              ? "No agent endpoint requests match this view."
              : "No activity matches this view."
          }}
        </p>
        <div
          v-for="event in events"
          :key="event.id"
          class="border-t pt-3 dark:border-slate-700"
        >
          <div
            v-if="event.endpoint"
            class="mb-2 flex flex-wrap items-center gap-3"
          >
            <code class="break-all font-semibold"
              >{{ event.endpoint.method }} {{ event.endpoint.path }}</code
            >
            <span v-if="event.endpoint.status" class="text-sm"
              >HTTP {{ event.endpoint.status }}</span
            >
            <span v-if="event.endpoint.tool" class="text-sm"
              >Tool: {{ event.endpoint.tool }}</span
            >
            <span v-else-if="event.endpoint.rpc_method" class="text-sm"
              >MCP: {{ event.endpoint.rpc_method }}</span
            >
          </div>
          <div class="flex flex-wrap justify-between gap-2 text-sm">
            <span
              >{{ event.event.replaceAll("_", " ") }} ·
              <strong>{{ event.outcome }}</strong></span
            ><time
              >{{ date(event.occurred_at) }} ·
              {{ event.actor?.name || "Unknown actor" }}</time
            >
          </div>
          <p class="mt-1 text-sm">
            {{ event.operation ? label(event.operation) : "" }}
          </p>
          <details class="mt-1">
            <summary class="cursor-pointer text-xs text-slate-500">
              Request {{ event.request_id }} · {{ event.ip_address }} · Details
            </summary>
            <pre
              class="overflow-auto whitespace-pre-wrap break-words p-2 text-xs"
              >{{ JSON.stringify(event.metadata, null, 2) }}</pre
            >
          </details>
        </div>
        <div class="flex items-center gap-4">
          <button
            class="btn"
            :disabled="page <= 1 || busy"
            @click="
              page--;
              run(loadActivity);
            "
          >
            Previous</button
          ><span class="text-sm">Page {{ page }} of {{ lastPage }}</span
          ><button
            class="btn"
            :disabled="page >= lastPage || busy"
            @click="
              page++;
              run(loadActivity);
            "
          >
            Next
          </button>
        </div>
      </section>
    </template>
  </main>
</template>

<style scoped>
.agent-input {
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.6rem;
  border: 1px solid #94a3b8;
  border-radius: 0.4rem;
  color: inherit;
  background: transparent;
}
</style>
